import {AlertIcon, SearchIcon} from '@primer/octicons-react'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {Blankslate, SkeletonText} from '@primer/react/experimental'

import {AIResourceResults, type AIResourceResult} from './AIResourceResults'
import styles from './AIResourceSearchPage.module.css'
import {ResourceTypeFilter, type ResourceTypeFilterProps} from './ResourceTypeFilter'
import {SearchCategoryNav, type AIResourceSearchCategory} from './SearchCategoryNav'
import {SearchResultsHeader} from './SearchResultsHeader'

export type AIResourceSearchPageState =
  | {status: 'success'; results: readonly AIResourceResult[]; totalCount: number}
  | {status: 'loading'}
  | {status: 'empty'}
  | {status: 'error'}

export type AIResourceSearchPageProps = {
  categories: readonly AIResourceSearchCategory[]
  filters: ResourceTypeFilterProps
  query: string
  state: AIResourceSearchPageState
}

export function AIResourceSearchPage({categories, filters, query, state}: AIResourceSearchPageProps) {
  return (
    <FilteredListPageLayout>
      <FilteredListPageLayout.Header>
        <FilteredListPageLayout.PageHeader>
          <FilteredListPageLayout.PageHeader.TitleArea>
            <FilteredListPageLayout.PageHeader.Title as="h1">AI resources</FilteredListPageLayout.PageHeader.Title>
          </FilteredListPageLayout.PageHeader.TitleArea>
        </FilteredListPageLayout.PageHeader>
      </FilteredListPageLayout.Header>

      <FilteredListPageLayout.Sidebar aria-label="Search filters" dialogTitle="Search filters" scrollable>
        <SearchCategoryNav categories={categories} />
        <ResourceTypeFilter {...filters} />
      </FilteredListPageLayout.Sidebar>

      <FilteredListPageLayout.Content as="div">
        <SearchResultsHeader
          query={query}
          status={state.status}
          totalCount={state.status === 'success' ? state.totalCount : undefined}
        />
        <FilteredListPageLayout.Results aria-label="AI resource results">
          <ResultsContent state={state} />
        </FilteredListPageLayout.Results>
      </FilteredListPageLayout.Content>
    </FilteredListPageLayout>
  )
}

function ResultsContent({state}: {state: AIResourceSearchPageState}) {
  switch (state.status) {
    case 'success':
      return <AIResourceResults results={state.results} />
    case 'loading':
      return <LoadingResults />
    case 'empty':
      return (
        <Blankslate border spacious>
          <Blankslate.Visual>
            <SearchIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading as="h3">No AI resources found</Blankslate.Heading>
          <Blankslate.Description>Try a different search term.</Blankslate.Description>
        </Blankslate>
      )
    case 'error':
      return (
        <Blankslate border spacious>
          <Blankslate.Visual>
            <AlertIcon size={24} />
          </Blankslate.Visual>
          <Blankslate.Heading as="h3">AI resource search is unavailable</Blankslate.Heading>
          <Blankslate.Description>Try again later.</Blankslate.Description>
        </Blankslate>
      )
  }
}

function LoadingResults() {
  return (
    <div className={styles.LoadingList} aria-hidden="true">
      <LoadingResultRow />
      <LoadingResultRow />
      <LoadingResultRow />
    </div>
  )
}

function LoadingResultRow() {
  return (
    <div className={styles.LoadingRow}>
      <SkeletonText size="titleMedium" />
      <SkeletonText size="bodyMedium" lines={2} />
    </div>
  )
}
